export const GUEST_CONVERSATION_KEY = "venaqqi_don_d_conversation_v1";
export const USER_CONVERSATION_KEY_PREFIX = "venaqqi_don_d_conversation_user_v1:";
export const CONVERSATION_SCHEMA_VERSION = 1;

// An authenticated identity is always the Supabase auth UUID. Email and device
// identifiers are deliberately not accepted because either can be shared.
export const conversationIdentity = user => {
  const userId = user?.userId || user?.id;
  return userId ? `user:${userId}` : "guest";
};

export const conversationStorageKey = user => {
  const identity = conversationIdentity(user);
  return identity === "guest" ? GUEST_CONVERSATION_KEY : `${USER_CONVERSATION_KEY_PREFIX}${identity.slice(5)}`;
};

export const createConversation = (user = null) => ({
  schemaVersion: CONVERSATION_SCHEMA_VERSION,
  messages: [], preferences: {}, intent: "", place: null, recommendation: null,
  coupon: null, currentQuestion: "", rejectedPlaces: [], owner: conversationIdentity(user), updatedAt: Date.now(),
});

const normalizeMessage = message => {
  if (!message || typeof message !== "object") return null;
  if (message.role !== "user" && message.role !== "assistant") return null;
  if (typeof message.content !== "string" || message.content === "__RETRY__") return null;
  const content = message.content.slice(0, 6000);
  if (!content.trim()) return null;
  return {
    role: message.role,
    content,
    ...(message.localOnly === true ? { localOnly: true } : {}),
    ...(message.conversion === true ? { conversion: true } : {}),
  };
};

export const normalizeConversationMessages = messages => (Array.isArray(messages) ? messages : [])
  .map(normalizeMessage)
  .filter(Boolean)
  .slice(-24);

export const readConversation = (user = null, storage = window.localStorage) => {
  const empty = createConversation(user);
  try {
    const value = JSON.parse(storage.getItem(conversationStorageKey(user)) || "null");
    // Reject copied, legacy, or manipulated records whose owner does not match.
    return value?.schemaVersion === CONVERSATION_SCHEMA_VERSION && value?.owner === empty.owner
      ? { ...empty, ...value, messages: normalizeConversationMessages(value.messages) }
      : empty;
  } catch { return empty; }
};

export const writeConversation = (conversation, user = null, storage = window.localStorage) => {
  const empty = createConversation(user);
  const source = conversation && typeof conversation === "object" ? conversation : {};
  const next = { ...empty, ...source, owner: empty.owner, messages: normalizeConversationMessages(source.messages), updatedAt: Date.now() };
  try { storage.setItem(conversationStorageKey(user), JSON.stringify(next)); } catch { /* storage unavailable */ }
  return next;
};

export const clearConversation = (user = null, storage = window.localStorage) => {
  const empty = createConversation(user);
  try { storage.setItem(conversationStorageKey(user), JSON.stringify(empty)); } catch { /* storage unavailable */ }
  return empty;
};

export const compactMessagesForModel = messages => {
  const usable = normalizeConversationMessages(messages).filter(message => !message.localOnly);
  return usable.slice(-10).map(message => ({ role: message.role, content: String(message.content).slice(0, 1200) }));
};

export const estimateContextCharacters = messages => compactMessagesForModel(messages).reduce((sum, message) => sum + message.content.length, 0);
